import type { CausaleAssenza, DataISO, Dipendente, Evento, GiornoCalendario } from '../api/tipi.ts';
import { giorniDellIntervallo } from '../calendario/griglia.ts';

/**
 * Calcoli dell'analisi dati.
 *
 * Tutto in ore, non in giorni. Nel file attuale il riepilogo mescola "giorni
 * ferie" e "ore permessi" nella stessa tabella, e i due numeri non si possono
 * sommare: non si sa quanto sia stata assente una persona senza fare i conti a
 * mano. Qui una giornata intera vale le ore dichiarate dal tipo (8) e tutto
 * diventa confrontabile.
 *
 * Restano visibili anche i giorni, perche' e' come le persone contano le
 * ferie, ma il totale che permette i confronti e' quello in ore.
 */

/** Ore che vale una giornata intera a tempo pieno. Rispecchia `tipi_evento`. */
export const ORE_GIORNALIERE = 8;

/** L'orario di chi non ha niente di dichiarato: otto ore tutti i giorni. */
export const ORARIO_TEMPO_PIENO = [8, 8, 8, 8, 8, 8, 8];

/**
 * Quante ore lavora questa persona in questa data. Zero se non lavora.
 *
 * Un part-time da sei ore che prende un giorno di ferie consuma sei ore, non
 * otto: contarle tutte uguali gonfierebbe i suoi totali di un terzo e
 * renderebbe incomparabili proprio i confronti per cui esiste questa pagina.
 * E il giorno conta: c'e' chi lavora tre ore dal lunedi' al venerdi' e due il
 * sabato, e negli uffici il fine settimana vale zero.
 *
 * Deve restare allineata a `oreDelGiorno` del backend (core/tempo.ts).
 */
export function oreDiUnaGiornata(
  dipendente: Pick<Dipendente, 'orarioSettimanale'>,
  data: DataISO,
): number {
  // getUTCDay numera da 0 (domenica); l'orario da lunedi'.
  const giorno = new Date(`${data}T00:00:00Z`).getUTCDay();
  const indice = giorno === 0 ? 6 : giorno - 1;
  const orario = dipendente.orarioSettimanale ?? ORARIO_TEMPO_PIENO;
  return orario[indice] ?? 0;
}

export interface RigaAnalisi {
  dipendenteId: string;
  dipendente: string;
  sede: string;
  reparto: string;
  /** Chiave `AAAA-MM`, oppure `AAAA` per il totale annuale. */
  periodo: string;
  giorniFerie: number;
  oreFerie: number;
  oreRecupero: number;
  ore104: number;
  oreMf: number;
  oreDs: number;
  oreMalattia: number;
  oreCp: number;
  giorniRiposo: number;
  oreTotali: number;
}

interface Accumulo {
  giorniFerie: number;
  oreFerie: number;
  oreRecupero: number;
  ore104: number;
  oreMf: number;
  oreDs: number;
  oreMalattia: number;
  oreCp: number;
  giorniRiposo: number;
  /* Le ore che valgono i giorni interi: dipendono dall'orario di chi li
     prende, e dal giorno della settimana, quindi si sommano man mano invece
     di moltiplicare alla fine il numero dei giorni per un'unica giornata. */
  oreDaGiorniFerie: number;
  oreDaGiorniRiposo: number;
}

function vuoto(): Accumulo {
  return {
    giorniFerie: 0, oreFerie: 0, oreRecupero: 0, ore104: 0, oreMf: 0, oreDs: 0,
    oreMalattia: 0, oreCp: 0, giorniRiposo: 0, oreDaGiorniFerie: 0, oreDaGiorniRiposo: 0,
  };
}

/**
 * Il totale con cui si fanno i confronti. **Senza 104, MF, CP, DS e malattia.**
 *
 * Quelle due non sono assenze concesse: sono diritti esercitati, approvati
 * all'istante proprio perche' nessuno ci decide sopra (punto 28). Sommarle
 * qui dentro produceva un totale che cresce quando una persona usa la 104, e
 * quel totale finisce nella colonna delle ore per reparto, nella media a
 * persona e nel grafico dei mesi: un confronto in cui chi assiste un
 * familiare disabile risulta piu' assente degli altri. Il numero non era
 * sbagliato in aritmetica, lo era come metro di paragone.
 *
 * Donazione sangue e malattia seguono la stessa logica (decisione 51):
 * nessuno le concede, e chi e' stato malato non deve risultare quello che
 * si assenta di piu'. Cosi' il congedo di paternita' (decisione 64).
 *
 * Restano contate a parte, nelle loro colonne: non si nascondono, non si
 * sommano.
 *
 * Il riposo produzione ci resta dentro: e' la produzione che si ferma, non
 * una scelta della persona, ma occupa comunque una giornata in cui non si e'
 * lavorato. Se anche quello dovesse uscire, si toglie qui.
 */
function oreTotali(a: Accumulo): number {
  return a.oreDaGiorniFerie + a.oreFerie + a.oreRecupero + a.oreDaGiorniRiposo;
}

const ORDINE_CAUSALE: Record<CausaleAssenza, keyof Accumulo> = {
  FERIE: 'oreFerie',
  RECUPERO: 'oreRecupero',
  LEGGE_104: 'ore104',
  MF: 'oreMf',
  DS: 'oreDs',
  MALATTIA: 'oreMalattia',
  CP: 'oreCp',
};

/**
 * Suddivide un evento nei giorni lavorativi che occupa.
 *
 * Le festivita' non consumano ferie, il fine settimana si'. E' la stessa
 * regola che decide il preavviso, e deve restare la stessa anche qui,
 * altrimenti il totale dell'analisi non corrisponde a quello che l'utente si
 * e' visto approvare.
 */
function giorniLavorativiDi(
  evento: Evento,
  nonLavorativi: ReadonlySet<DataISO>,
  dipendente: Pick<Dipendente, 'orarioSettimanale'> | undefined,
): DataISO[] {
  return giorniDellIntervallo(evento.dataInizio, evento.dataFine).filter(
    (g) =>
      !nonLavorativi.has(g) &&
      (dipendente === undefined || oreDiUnaGiornata(dipendente, g) > 0),
  );
}

function accumula(
  acc: Accumulo,
  evento: Evento,
  giorni: readonly DataISO[],
  dipendente: Pick<Dipendente, 'orarioSettimanale'> | undefined,
): void {
  if (evento.stato === 'ANNULLATO' || evento.stato === 'RIFIUTATO') return;

  const ore = (giorno: DataISO) =>
    dipendente === undefined ? ORE_GIORNALIERE : oreDiUnaGiornata(dipendente, giorno);

  if (evento.tipo === 'FERIE') {
    acc.giorniFerie += giorni.length;
    for (const g of giorni) acc.oreDaGiorniFerie += ore(g);
  } else if (evento.tipo === 'RIPOSO_PRODUZIONE') {
    acc.giorniRiposo += giorni.length;
    for (const g of giorni) acc.oreDaGiorniRiposo += ore(g);
  } else if (evento.tipo === 'ORE_FERIE' && evento.ore !== null) {
    const campo = ORDINE_CAUSALE[evento.causale ?? 'FERIE'] ?? 'oreFerie';
    acc[campo] += evento.ore;
  }
}

/**
 * I giorni che non consumano ferie.
 *
 * Solo le festivita': la settimana lavorativa va da lunedi' a domenica,
 * perche' in stabilimento si lavora anche nel fine settimana. Il sabato e la
 * domenica restano colorati a parte nella griglia, ma quello aiuta a leggere,
 * non a contare.
 *
 * Deve restare allineata a `giorniLavorativi` del backend: se le due
 * divergono, il totale dell'analisi non corrisponde piu' a quello che la
 * persona si e' vista approvare.
 */
export function insiemeNonLavorativi(
  giorni: readonly GiornoCalendario[],
): ReadonlySet<DataISO> {
  return new Set(giorni.filter((g) => g.festivita).map((g) => g.data));
}

/**
 * Righe per dipendente e mese, piu' il totale annuale di ciascuno.
 *
 * Un evento a cavallo di due mesi viene diviso: i suoi giorni finiscono nel
 * mese a cui appartengono davvero. Attribuirlo tutto al mese di inizio
 * falserebbe i totali di gennaio ogni anno.
 */
export function analizza(
  dipendenti: readonly Dipendente[],
  eventi: readonly Evento[],
  giorni: readonly GiornoCalendario[],
  anno: number,
): RigaAnalisi[] {
  const nonLavorativi = insiemeNonLavorativi(giorni);
  const perPersona = new Map<string, Map<string, Accumulo>>();
  // L'orario di ciascuno decide quali giorni gli contano come assenza: negli
  // uffici il fine settimana non conta, in produzione si'.
  const anagrafica = new Map(dipendenti.map((d) => [d.id, d]));

  for (const evento of eventi) {
    const chi = anagrafica.get(evento.dipendenteId);
    const lavorativi = giorniLavorativiDi(evento, nonLavorativi, chi).filter((g) =>
      g.startsWith(String(anno)),
    );
    if (lavorativi.length === 0 && evento.tipo !== 'ORE_FERIE') continue;

    const perMese = perPersona.get(evento.dipendenteId) ?? new Map<string, Accumulo>();
    perPersona.set(evento.dipendenteId, perMese);

    if (evento.tipo === 'ORE_FERIE') {
      if (!evento.dataInizio.startsWith(String(anno))) continue;
      const mese = evento.dataInizio.slice(0, 7);
      const acc = perMese.get(mese) ?? vuoto();
      perMese.set(mese, acc);
      accumula(acc, evento, [evento.dataInizio], chi);
      continue;
    }

    // I giorni si raggruppano per mese di appartenenza, non per mese di
    // inizio: un'assenza dal 28 gennaio al 3 febbraio conta su entrambi.
    const perMeseEvento = new Map<string, DataISO[]>();
    for (const g of lavorativi) {
      const mese = g.slice(0, 7);
      perMeseEvento.set(mese, [...(perMeseEvento.get(mese) ?? []), g]);
    }
    for (const [mese, elenco] of perMeseEvento) {
      const acc = perMese.get(mese) ?? vuoto();
      perMese.set(mese, acc);
      accumula(acc, evento, elenco, chi);
    }
  }

  const righe: RigaAnalisi[] = [];
  for (const dipendente of dipendenti) {
    const perMese = perPersona.get(dipendente.id);
    if (!perMese) continue;

    const totale = vuoto();
    for (const [mese, acc] of [...perMese].sort((a, b) => a[0].localeCompare(b[0]))) {
      righe.push(riga(dipendente, mese, acc));
      totale.giorniFerie += acc.giorniFerie;
      totale.oreFerie += acc.oreFerie;
      totale.oreRecupero += acc.oreRecupero;
      totale.ore104 += acc.ore104;
      totale.oreMf += acc.oreMf;
      totale.oreDs += acc.oreDs;
      totale.oreMalattia += acc.oreMalattia;
      totale.oreCp += acc.oreCp;
      totale.giorniRiposo += acc.giorniRiposo;
      totale.oreDaGiorniFerie += acc.oreDaGiorniFerie;
      totale.oreDaGiorniRiposo += acc.oreDaGiorniRiposo;
    }
    righe.push(riga(dipendente, String(anno), totale));
  }
  return righe;
}

function riga(dipendente: Dipendente, periodo: string, acc: Accumulo): RigaAnalisi {
  return {
    dipendenteId: dipendente.id,
    dipendente: dipendente.nomeCompleto,
    sede: dipendente.sedeNome,
    reparto: dipendente.repartoNome,
    periodo,
    giorniFerie: arrotonda(acc.giorniFerie),
    oreFerie: arrotonda(acc.oreFerie),
    oreRecupero: arrotonda(acc.oreRecupero),
    ore104: arrotonda(acc.ore104),
    oreMf: arrotonda(acc.oreMf),
    oreDs: arrotonda(acc.oreDs),
    oreMalattia: arrotonda(acc.oreMalattia),
    oreCp: arrotonda(acc.oreCp),
    giorniRiposo: arrotonda(acc.giorniRiposo),
    oreTotali: arrotonda(oreTotali(acc)),
  };
}

function arrotonda(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Le righe da sommare per i riquadri di riepilogo.
 *
 * `analizza` produce, per ogni dipendente, una riga per mese piu' una riga di
 * totale annuale che e' la somma delle mensili. Sommarle tutte insieme conta
 * ogni valore due volte, una come mese e una dentro il totale: i riquadri
 * mostravano il doppio del vero ogni volta che la tabella era in dettaglio
 * mensile senza un mese scelto. Un numero doppio, su una pagina che serve a
 * decidere, non ha nessun segnale che avverta chi lo legge.
 *
 * Le due famiglie non si mescolano mai: o il mese chiesto, o l'anno.
 */
export function righeDeiTotali(
  righe: readonly RigaAnalisi[],
  anno: number,
  mese: string,
): RigaAnalisi[] {
  const periodo = mese === '' ? String(anno) : `${anno}-${mese}`;
  return righe.filter((r) => r.periodo === periodo);
}

/** Ore di assenza per mese, sommate su tutti i dipendenti filtrati. */
export function distribuzioneMensile(righe: readonly RigaAnalisi[], anno: number): number[] {
  const mesi = new Array<number>(12).fill(0);
  for (const r of righe) {
    if (r.periodo === String(anno)) continue; // e' il totale, non un mese
    const indice = Number(r.periodo.slice(5, 7)) - 1;
    if (indice >= 0 && indice < 12) mesi[indice] = (mesi[indice] ?? 0) + r.oreTotali;
  }
  return mesi;
}

export interface GiornoScoperto {
  data: DataISO;
  reparto: string;
  assenti: number;
  organico: number;
}

/**
 * I giorni con piu' assenti nello stesso reparto.
 *
 * E' l'informazione che il file Excel non riesce a dare senza scorrere a mano
 * la griglia, e serve a scoprire una scopertura prima che arrivi, non il
 * giorno stesso.
 */
export function giorniPiuScoperti(
  dipendenti: readonly Dipendente[],
  eventi: readonly Evento[],
  giorni: readonly GiornoCalendario[],
  /**
   * Il periodo da guardare: `AAAA` per l'anno intero, `AAAA-MM` per un mese.
   *
   * I filtri in cima alla pagina valgono per tutte le viste tranne questa, e
   * chi sceglieva un mese vedeva comunque i dieci giorni peggiori dell'anno:
   * sembrava che il filtro non funzionasse.
   */
  periodo = '',
  quanti = 10,
): GiornoScoperto[] {
  const nonLavorativi = insiemeNonLavorativi(giorni);
  const anagrafica = new Map(dipendenti.map((d) => [d.id, d]));
  const repartoDi = new Map(dipendenti.map((d) => [d.id, `${d.sedeNome} · ${d.repartoNome}`]));
  const organico = new Map<string, number>();
  for (const d of dipendenti) {
    const chiave = `${d.sedeNome} · ${d.repartoNome}`;
    organico.set(chiave, (organico.get(chiave) ?? 0) + 1);
  }

  const conteggio = new Map<string, number>();
  for (const evento of eventi) {
    if (evento.stato === 'ANNULLATO' || evento.stato === 'RIFIUTATO') continue;
    if (evento.tipo === 'SPOSTAMENTO' || evento.tipo === 'NOTA') continue;
    const reparto = repartoDi.get(evento.dipendenteId);
    if (!reparto) continue;
    const chi = anagrafica.get(evento.dipendenteId);
    for (const g of giorniDellIntervallo(evento.dataInizio, evento.dataFine)) {
      if (nonLavorativi.has(g)) continue;
      if (periodo !== '' && !g.startsWith(periodo)) continue;
      // Un giorno che quella persona non lavora non lascia scoperto niente:
      // la domenica di un impiegato non e' un buco nel turno (decisione 54).
      if (chi !== undefined && oreDiUnaGiornata(chi, g) === 0) continue;
      const chiave = `${g}|${reparto}`;
      conteggio.set(chiave, (conteggio.get(chiave) ?? 0) + 1);
    }
  }

  return [...conteggio]
    .map(([chiave, assenti]) => {
      const [data, reparto] = chiave.split('|') as [DataISO, string];
      return { data, reparto, assenti, organico: organico.get(reparto) ?? 0 };
    })
    .sort((a, b) => b.assenti - a.assenti || a.data.localeCompare(b.data))
    .slice(0, quanti);
}

/**
 * Totali per reparto, per il confronto fra squadre.
 *
 * Il periodo e' `AAAA` per l'anno intero o `AAAA-MM` per un mese: e' lo stesso
 * filtro degli altri riquadri, e con l'anno fisso un reparto in difficolta' a
 * luglio spariva dentro la media dei dodici mesi.
 */
export function totaliPerReparto(
  righe: readonly RigaAnalisi[],
  periodo: string,
): { reparto: string; persone: number; oreTotali: number; oreMedie: number }[] {
  const perReparto = new Map<string, { persone: Set<string>; ore: number }>();
  for (const r of righe) {
    if (r.periodo !== periodo) continue;
    const chiave = `${r.sede} · ${r.reparto}`;
    const voce = perReparto.get(chiave) ?? { persone: new Set<string>(), ore: 0 };
    voce.persone.add(r.dipendenteId);
    voce.ore += r.oreTotali;
    perReparto.set(chiave, voce);
  }

  return [...perReparto]
    .map(([reparto, v]) => ({
      reparto,
      persone: v.persone.size,
      oreTotali: arrotonda(v.ore),
      oreMedie: v.persone.size > 0 ? arrotonda(v.ore / v.persone.size) : 0,
    }))
    .sort((a, b) => b.oreTotali - a.oreTotali);
}

/**
 * Le righe mensili di una persona, in ordine di mese.
 *
 * Il totale annuale resta fuori: qui serve il dettaglio, e mescolarlo con la
 * riga che li somma tutti farebbe leggere due volte gli stessi numeri.
 */
export function mesiDi(
  righe: readonly RigaAnalisi[],
  dipendenteId: string,
  anno: number,
): RigaAnalisi[] {
  return righe
    .filter((r) => r.dipendenteId === dipendenteId && r.periodo !== String(anno))
    .sort((a, b) => a.periodo.localeCompare(b.periodo));
}

export const NOMI_MESI = [
  'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
  'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic',
];
